import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Mail, Megaphone, MessageCircle } from "lucide-react";
import { FaFacebook, FaInstagram, FaSnapchat, FaXTwitter, FaYoutube } from "react-icons/fa6";
import FormField from "../../Common/FormField/FormField";
import { getContactDetails, updateContactDetails } from "../../../redux/setting/action";
import "../../Common/FormShell/FormShell.css";
import "./ContactDetails.css";

/**
 * Settings → Contact Details: the one record the website footer, the
 * Customer Care page, the app and every order email read. Two sections —
 * how customers reach the store (an email and one WhatsApp number), then
 * the social profiles.
 *
 * The landline and country-code boxes are gone. Nothing printed the
 * landline (the emails' SUPPORT_LANDLINE has no template, the site shows
 * the WhatsApp number), and the number carries its own country code. Both
 * columns are cleared with every save, so the record never holds a value
 * nobody can see or edit here.
 */

const REACH = [
  {
    name: "email_id",
    label: "Email",
    type: "email",
    icon: Mail,
    placeholder: "support@nysmah.com",
    hint: "In the footer, on Customer Care, and where customers reply to order emails.",
  },
  {
    name: "whatsapp_no",
    label: "WhatsApp number",
    type: "tel",
    icon: MessageCircle,
    required: true,
    placeholder: "+966 5X XXX XXXX",
    hint: "One number for calls and WhatsApp chats, with the country code.",
  },
];

const SOCIALS = [
  { name: "instagram", label: "Instagram", icon: FaInstagram, placeholder: "https://www.instagram.com/…" },
  { name: "snapchat", label: "Snapchat", icon: FaSnapchat, placeholder: "https://www.snapchat.com/add/…" },
  { name: "x", label: "X", icon: FaXTwitter, placeholder: "https://x.com/…" },
  { name: "facebook", label: "Facebook", icon: FaFacebook, placeholder: "https://www.facebook.com/…" },
  { name: "youtube", label: "YouTube", icon: FaYoutube, placeholder: "https://www.youtube.com/@…" },
];

const FIELDS = [...REACH, ...SOCIALS].map((field) => field.name);

/** Columns this screen no longer shows, cleared with every save. */
const RETIRED = { landline_no: "", country_code: "" };

/** A country code and digits, with the spaces, dashes and brackets people type. */
const PHONE = /^\+?[0-9][0-9 ()-]{6,19}$/;

const schema = Yup.object({
  email_id: Yup.string().trim().email("Enter a valid email address"),
  whatsapp_no: Yup.string()
    .trim()
    .required("The WhatsApp number is required")
    .matches(PHONE, "Digits with the country code, like +966 55 123 4567"),
  ...Object.fromEntries(
    SOCIALS.map(({ name }) => [
      name,
      Yup.string().trim().url("Paste the full link, starting with https://"),
    ]),
  ),
});

/** The stored record as form values: every box present, nulls as "". */
const valuesOf = (record) =>
  Object.fromEntries(FIELDS.map((name) => [name, (record && record[name]) || ""]));

export default function ContactDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const record = useSelector((state) => state.reusableSlice.CustomerDetails);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.resolve(dispatch(getContactDetails())).finally(() => setLoading(false));
  }, [dispatch]);

  const initialValues = useMemo(() => valuesOf(record), [record]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      const trimmed = Object.fromEntries(FIELDS.map((name) => [name, values[name].trim()]));
      const saved = await dispatch(updateContactDetails({ ...trimmed, ...RETIRED }, setSubmitting));
      // The save answers with a message, not the record: read it back so the
      // form settles on what is stored and the Save button rests.
      if (saved) dispatch(getContactDetails());
    },
  });

  const field = ({ name, label, type = "url", icon: Icon, required = false, placeholder, hint }) => {
    const invalid = Boolean(formik.touched[name] && formik.errors[name]);
    return (
      <FormField
        key={name}
        name={name}
        required={required}
        hint={hint ? t(hint) : undefined}
        error={formik.errors[name]}
        touched={formik.touched[name]}
        label={
          <span className="cd-label">
            <Icon className="cd-label__icon" aria-hidden="true" />
            {t(label)}
          </span>
        }
      >
        <input
          id={name}
          name={name}
          type={type}
          dir="ltr"
          className={`nb-input${invalid ? " is-invalid" : ""}`}
          placeholder={placeholder}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          autoComplete="off"
          spellCheck={false}
        />
      </FormField>
    );
  };

  if (loading) {
    return (
      <div className="nb-page cd-page">
        <div className="cd-shell">
          <p className="cd-loading">{t("Loading contact details…")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nb-page cd-page">
      <div className="cd-shell">
        <header className="nb-hero">
          <span className="nb-hero__eyebrow">
            <Megaphone size={12} aria-hidden="true" />
            {t("Shown to customers")}
          </span>
          <h1 className="nb-hero__title">{t("Contact Details")}</h1>
          <p className="nb-hero__sub">
            {t(
              "One record for the website footer, the Customer Care page, the app and every order email. Save it once and it shows everywhere.",
            )}
          </p>
        </header>

        <form className="nb-form" onSubmit={formik.handleSubmit} noValidate>
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">01</span>
              <div>
                <h2 className="nb-section__title">{t("How customers reach you")}</h2>
                <p className="nb-section__hint">
                  {t("Printed under “Do you need help?” at the foot of every page.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">{REACH.map(field)}</div>
            </div>
          </section>

          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">02</span>
              <div>
                <h2 className="nb-section__title">
                  {t("Social profiles")}
                  <span className="nb-section__optional">{t("Optional")}</span>
                </h2>
                <p className="nb-section__hint">
                  {t("Paste the full link to each profile. The footer shows an icon for each one you fill in.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">{SOCIALS.map(field)}</div>
            </div>
          </section>

          <footer className="cd-actions">
            <p className={`cd-actions__note${formik.dirty ? " is-dirty" : ""}`}>
              {formik.dirty
                ? t("Unsaved changes")
                : t("Saved changes show on the website right away.")}
            </p>
            <button
              type="submit"
              className="nb-btn nb-btn--primary"
              disabled={formik.isSubmitting || !formik.dirty}
            >
              {formik.isSubmitting ? t("Saving…") : t("Save changes")}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
